import { DashboardButtonV2 } from '@/components/Dashboard/DashboardButtonV2'
import CTAButton from '@/components/Landing/CTAButton'
import { PaymentErrorBanner } from '@/components/Pricing/PaymentErrorBanner'
import { UpgradeBanner } from '@/components/Pricing/UpgradeBanner'
import { useGetUserQuery } from '@/lib/features/user/userApi'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'

const Header = ({ scrollRef }: { scrollRef?: React.RefObject<HTMLDivElement> }) => {
  const { data: userData, isLoading: userLoading } = useGetUserQuery()
  const user = userData?.user
  const state = userLoading ? 'loading' : 'authenticated'
  const [scrolled, setScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const container = scrollRef?.current
    if (!container) return
    const handleScroll = () => {
      setScrolled(container.scrollTop > 0)
    }
    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [scrollRef])

  const handleTemplatesClick = (e: React.MouseEvent) => {
    e.preventDefault()
    const container = scrollRef?.current
    if (!container) return
    const templatesSection = document.getElementById('templates-section')
    if (!templatesSection) return
    container.scrollTo({
      top: templatesSection.offsetTop - 100,
      behavior: 'smooth',
    })
    setIsMobileMenuOpen(false)
  }

  const handleCapabilitiesClick = (e: React.MouseEvent) => {
    e.preventDefault()
    const container = scrollRef?.current
    if (!container) return
    const capabilitiesSection = document.getElementById('capabilities-section')
    if (!capabilitiesSection) return
    container.scrollTo({
      top: capabilitiesSection.offsetTop - 100,
      behavior: 'smooth',
    })
    setIsMobileMenuOpen(false)
  }

  const navLinks = [
    { href: '/pricing', label: 'Pricing' },
    { href: 'https://pages.sprites.ai/', label: 'Blog', external: true },
    { onClick: handleTemplatesClick, label: 'Templates' },
    { onClick: handleCapabilitiesClick, label: 'Capabilities' },
  ]

  return (
    <>
      <header
        className={cn(
          'fixed left-0 top-0 z-50 flex w-full items-center justify-start gap-9 px-4 pb-2 pt-2 transition-all duration-300 md:px-[35px]',
          scrolled ? 'bg-neutral-50/80 backdrop-blur-lg' : 'bg-transparent'
        )}
      >
        {/* Hide logo/brand on mobile when menu is open */}
        <div className={`flex items-center ${isMobileMenuOpen ? 'invisible' : ''} md:visible`}>
          <Image
            src="/sprites.svg"
            alt="Sprites"
            className="h-[40px] w-[40px] md:h-[52px] md:w-[52px]"
            width={52}
            height={52}
          />
          <span className="!font-libre-baskerville py-[6px] pl-[6px] text-[20px] font-normal md:text-[24px]">
            Sprites AI
          </span>
        </div>

        {/* Desktop Navigation */}
        <nav className="!font-libre-baskerville hidden items-center gap-[36px] text-center text-[16px] font-normal capitalize leading-[100%] text-[#191919] md:flex">
          {navLinks.map((link, index) => (
            <a
              key={index}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              onClick={link.onClick}
              className="cursor-pointer transition-opacity duration-200 hover:opacity-70"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex-1"></div>

        {/* Desktop CTA Buttons */}
        <div className="hidden w-auto items-center justify-end space-x-4 md:flex">
          {state === 'authenticated' && user && !user.isAnonymous && <DashboardButtonV2 />}
          {state === 'authenticated' && user && user.isAnonymous && (
            <>
              <a
                href="https://calendly.com/anna-cher-campfire/sprites-demo"
                target="_blank"
                rel="noopener noreferrer"
              >
                <CTAButton outlined>Book a demo</CTAButton>
              </a>
              <CTAButton>Join for free</CTAButton>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        {!isMobileMenuOpen && (
          <button
            className="z-50 -mr-2 p-2 text-[#191919] md:hidden"
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        )}
      </header>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 20 }}
            className="fixed right-0 top-0 z-[100] flex h-full w-[85vw] max-w-[340px] flex-col bg-white shadow-lg md:hidden"
          >
            {/* Header inside mobile menu */}
            <div className="z-50 flex items-center justify-between border-b border-neutral-100 px-6 pb-2 pt-6">
              <div className="flex items-center">
                <Image
                  src="/sprites.svg"
                  alt="Sprites"
                  className="h-[36px] w-[36px]"
                  width={36}
                  height={36}
                />
                <span className="!font-libre-baskerville pl-[8px] text-[20px] font-normal">
                  Sprites AI
                </span>
              </div>
              <button
                className="pointer-events-auto z-50 p-3 focus:outline-none"
                style={{ minWidth: 44, minHeight: 44 }}
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            {/* Nav links */}
            <nav className="!font-libre-baskerville flex flex-col space-y-6 px-6 pt-8 text-[18px] font-normal capitalize text-[#191919]">
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={link.onClick}
                  className="cursor-pointer py-2 transition-opacity duration-200 hover:opacity-70"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            {/* CTA buttons at the bottom, always visible on mobile */}
            <div className="mt-auto flex flex-col space-y-4 border-t border-neutral-100 px-6 pb-8 pt-12 md:hidden">
              {user && !user.isAnonymous ? (
                <DashboardButtonV2 className="w-full" />
              ) : (
                <>
                  <a
                    href="https://calendly.com/anna-cher-campfire/sprites-demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <CTAButton outlined className="w-full">
                      Book a demo
                    </CTAButton>
                  </a>
                  <CTAButton className="w-full">Join for free</CTAButton>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop for mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <UpgradeBanner />
      <PaymentErrorBanner />
    </>
  )
}

export default Header
